import {
  Component, OnInit, Output, EventEmitter, Input, SimpleChanges 
} from '@angular/core';
import { FormGroup, FormControl, Validators } from '@angular/forms';
import {
  combineLatest, Subject, switchMap, take, takeUntil 
} from 'rxjs';
import {
  activation_labels,
  regex,
  tooltip_delay,
  STATES,
} from '../../utils/constants.helper';
import { MemberService } from '../../services/member.service';
import { LoginService } from '../../services/login.service';
import { UserDetailsModel } from '../../models/userdetails.model';
import { MemberDetailsModel, MemberTypeFormDataModel } from '../../models/memberdetails.model';
import { MemberUpdateDetailsModel } from '../../models/memberupdatedetails.model';
import { constructRequest } from '../../utils/memberupdaterequest.helper';

@Component({
  selector: 'app-member-update',
  templateUrl: './member-update.component.html',
  styleUrls: ['./member-update.component.scss'],
})
export class MemberUpdateComponent implements OnInit {
  @Output() memberUpdateClick = new EventEmitter();

  @Input() isActiveStep: boolean = false;

  tooltipDelay: any = tooltip_delay;

  userInputs!: MemberTypeFormDataModel;

  memberAddressForm!: FormGroup;

  memberPreferenceForm!: FormGroup;

  userDetails!: UserDetailsModel;

  memberDetails!: MemberDetailsModel;

  memberUpdateDetails!: MemberUpdateDetailsModel;

  mailingAddress!: string;

  formError!: boolean;

  formMessage!: string;

  termsCheck: boolean = true;

  pageLoad: boolean = false;

  mailingAddressError: boolean = false;

  activationError: boolean = false;

  loading: boolean = false;

  states: Array<string> = STATES;

  private destroy$ = new Subject<void>();

  protected readonly labels = activation_labels.memberUpdate;

  constructor(
    private loginService: LoginService,
    private memberService: MemberService
  ) {}

  ngOnInit(): void {
    this.pageLoad = true;

    this.memberAddressForm = new FormGroup({
      unitNo: new FormControl(null, [
        Validators.pattern(regex.NUMBER_ALPHABETS_HYPHENS_REGEX),
      ]),
      streetNo: new FormControl(null, [
        Validators.required,
        Validators.pattern(regex.NUMBER_ALPHABETS_HYPHENS_REGEX),
      ]),
      streetName: new FormControl(null, [
        Validators.required,
        Validators.pattern(regex.ALPA_SPACE_HYPHENS_REGEX),
      ]),
      suburb: new FormControl(null, [
        Validators.required,
        Validators.pattern(regex.ALPABETS_REGEX),
      ]),
      postcode: new FormControl(null, [
        Validators.required,
        Validators.pattern(regex.NUMBER_REGEX),
      ]),
      state: new FormControl(null, [Validators.required]),
    });

    this.memberPreferenceForm = new FormGroup({
      email: new FormControl('', [
        Validators.required,
        Validators.pattern(regex.EMAIL_REGEX),
      ]),
      mobile: new FormControl('', [
        Validators.required,
        Validators.pattern(regex.MOBILE_NUMBER_REGEX),
      ]),
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['isActiveStep'] && this.isActiveStep) {
      this.loadData();
    }
  }

  loadData(): void {
    combineLatest([
      this.loginService.validatedUserDetails,
      this.memberService.memberInputs
    ]).pipe(
      take(1),
      switchMap(([userDetails, userInputs]) => {
        if (userDetails !== null) {
          this.userDetails = userDetails;
        }
        if (userInputs !== null) {
          this.userInputs = userInputs;
        }
        return this.memberService.fetchMemberDetails(
          this.userDetails?.employeeId ?? '',
          this.userDetails?.sdcCardNo ?? ''
        );
      }),
      takeUntil(this.destroy$)
    ).subscribe({
      next: (details: MemberDetailsModel) => {
        this.memberDetails = details;
        this.pageLoad = false;

        if (
          details.mailingStreetName === ''
          && details.mailingSuburb === ''
          && details.mailingState === ''
          && details.mailingPostcode === ''
          && this.userDetails.isSuccessFactorUser
        ) {
          this.mailingAddressError = true;
          this.mailingAddress = '-';
        } else {
          this.mailingAddress = `Unit ${details.mailingUnitNumber}, ${details.mailingStreetNumber} ${details.mailingStreetName},${details.mailingSuburb} ${details.mailingState} ${details.mailingPostcode}`;
        }

        if (!this.userDetails.isSuccessFactorUser) {
          this.memberAddressForm.get('unitNo')?.setValue(details.mailingUnitNumber);
          this.memberAddressForm.get('streetNo')?.setValue(details.mailingStreetNumber);
          this.memberAddressForm.get('streetName')?.setValue(details.mailingStreetName);
          this.memberAddressForm.get('suburb')?.setValue(details.mailingSuburb);
          this.memberAddressForm.get('postcode')?.setValue(details.mailingPostcode);
          this.memberAddressForm.get('state')?.setValue(details.mailingState);
        }
      },
      error: (error) => {
        this.formError = true;
        this.formMessage = typeof error === 'string' ? error : error.message || 'An error occurred';
        console.error('Error fetching member details:', error);
      }
    });
  }

  validateForm(): boolean {
    if (
      !this.userDetails.isSuccessFactorUser
      && this.memberAddressForm.valid
      && this.memberPreferenceForm.valid
    ) return false;
    if (
      this.userDetails.isSuccessFactorUser
      && this.memberPreferenceForm.valid
      && !this.mailingAddressError
    ) return false;
    return true;
  }

  toggleTermsCheck(value: boolean): void {
    this.termsCheck = value;
  }

  updateDetails(): void {
    this.loading = true;
    const memberUpdateRequest = constructRequest(
      this.memberDetails,
      this.termsCheck,
      this.userInputs,
      this.userDetails,
      this.memberAddressForm,
      this.memberPreferenceForm
    );
    this.memberService.submitMemberActivation(memberUpdateRequest).subscribe(
      (details) => {
        this.loading = false;
        this.memberUpdateClick.emit();
      },
      (error) => {
        this.formError = true;
        this.activationError = true;
        this.loading = false;
        this.formMessage = error;
      }
    );
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
